import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
// UPPDRAG-8 PR-C: dual-write helpers for the 1-week shadow window.
// sendGroupMessage / setGroupMessagePinned write to BOTH legacy
// group_chat_messages AND new chat_messages.
import { sendGroupMessage, setGroupMessagePinned } from "@/lib/chatDualWrite";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Send, Pin, PinOff, Users, MessageCircle } from "lucide-react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface GroupChatProps {
  groupId: string;
  groupName: string;
  memberCount?: number;
  isCoachOrAdmin?: boolean;
}

interface ChatMessage {
  id: string;
  group_id: string;
  sender_id: string;
  message: string;
  is_system: boolean;
  pinned: boolean;
  pinned_by: string | null;
  created_at: string;
  profiles: { full_name: string | null } | null;
}

export default function GroupChat({ groupId, groupName, memberCount, isCoachOrAdmin }: GroupChatProps) {
  const { user } = useAuth();
  const { roles } = useUserRole();
  const queryClient = useQueryClient();
  const [newMessage, setNewMessage] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const canManage = isCoachOrAdmin ?? (roles.includes("admin") || roles.includes("coach"));

  const { data: messages = [], isLoading } = useQuery({
    queryKey: ["group-chat", groupId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("group_chat_messages")
        .select("*, profiles:sender_id(full_name)")
        .eq("group_id", groupId)
        .order("created_at", { ascending: true })
        .limit(100);
      if (error) throw error;
      return ((data as unknown as ChatMessage[]) ?? []);
    },
    enabled: !!groupId,
  });

  // Realtime subscription
  useEffect(() => {
    if (!groupId) return;
    const channel = supabase
      .channel(`group-chat-${groupId}`)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "group_chat_messages",
        filter: `group_id=eq.${groupId}`,
      }, async (payload) => {
        const { data: sender } = await supabase
          .from("public_profiles" as any).select("full_name").eq("id", payload.new.sender_id).single();
        queryClient.setQueryData(["group-chat", groupId], (old: ChatMessage[] | undefined) => {
          const msg = { ...payload.new, profiles: sender } as ChatMessage;
          if (!old) return [msg];
          if (old.some(m => m.id === msg.id)) return old;
          return [...old, msg];
        });
      })
      .on("postgres_changes", {
        event: "UPDATE",
        schema: "public",
        table: "group_chat_messages",
        filter: `group_id=eq.${groupId}`,
      }, () => {
        queryClient.invalidateQueries({ queryKey: ["group-chat", groupId] });
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [groupId, queryClient]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // UPPDRAG-8 PR-C: dual-write to legacy + new schema.
  const sendMutation = useMutation({
    mutationFn: async (message: string) => {
      await sendGroupMessage(supabase, {
        groupId,
        senderUserId: user!.id,
        body: message,
      });
    },
    onError: (err: any) => { console.error(err); toast.error(err?.message || "Kunde inte skicka meddelandet"); },
  });

  const togglePinMutation = useMutation({
    mutationFn: async ({ id, pinned }: { id: string; pinned: boolean }) => {
      await setGroupMessagePinned(supabase, {
        legacyMessageId: id,
        pinned: !pinned,
        pinnedByUserId: user!.id,
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["group-chat", groupId] }),
  });

  const handleSend = () => {
    const trimmed = newMessage.trim();
    if (!trimmed) return;
    sendMutation.mutate(trimmed);
    setNewMessage("");
  };

  const pinnedMessages = messages.filter(m => m.pinned);

  return (
    <Card className="flex flex-col h-[500px]">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <MessageCircle className="h-5 w-5" />
          {groupName}
          {memberCount != null && (
            <Badge variant="secondary" className="ml-auto gap-1">
              <Users className="h-3 w-3" /> {memberCount}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col gap-2 overflow-hidden p-3 pt-0">
        {/* Pinned messages */}
        {pinnedMessages.length > 0 && (
          <div className="rounded-md bg-accent/50 p-2 space-y-1 shrink-0">
            {pinnedMessages.map(m => (
              <div key={m.id} className="flex items-start gap-1.5 text-xs">
                <Pin className="h-3 w-3 text-primary mt-0.5 shrink-0" />
                <span className="font-medium">{m.profiles?.full_name}:</span>
                <span className="text-muted-foreground">{m.message}</span>
              </div>
            ))}
          </div>
        )}

        {/* Messages */}
        <ScrollArea className="flex-1" ref={scrollRef as any}>
          <div className="space-y-2 pr-2">
            {isLoading ? (
              <p className="text-sm text-muted-foreground text-center py-8">Laddar chatt…</p>
            ) : messages.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">Inga meddelanden ännu</p>
            ) : (
              messages.map(m => (
                <div
                  key={m.id}
                  className={cn(
                    "group",
                    m.is_system && "text-center"
                  )}
                >
                  {m.is_system ? (
                    <p className="text-xs italic text-muted-foreground">{m.message}</p>
                  ) : (
                    <div className={cn(
                      "flex flex-col gap-0.5",
                      m.sender_id === user?.id ? "items-end" : "items-start"
                    )}>
                      <span className="text-[11px] text-muted-foreground">
                        {m.profiles?.full_name ?? "Okänd"} · {format(new Date(m.created_at), "d MMM HH:mm", { locale: sv })}
                      </span>
                      <div className={cn(
                        "rounded-lg px-3 py-1.5 text-sm max-w-[80%] relative",
                        m.sender_id === user?.id
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted"
                      )}>
                        {m.message}
                        {canManage && (
                          <button
                            onClick={() => togglePinMutation.mutate({ id: m.id, pinned: m.pinned })}
                            className="absolute -top-2 -right-2 hidden group-hover:flex h-5 w-5 items-center justify-center rounded-full bg-background border shadow-sm"
                            title={m.pinned ? "Avpinna" : "Pinna"}
                          >
                            {m.pinned ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </ScrollArea>

        {/* Input */}
        <div className="flex gap-2 shrink-0 pt-1">
          <Input
            value={newMessage}
            onChange={e => setNewMessage(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !e.shiftKey && handleSend()}
            placeholder="Skriv ett meddelande…"
            disabled={sendMutation.isPending}
          />
          <Button size="icon" onClick={handleSend} disabled={!newMessage.trim() || sendMutation.isPending}>
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
